import React, { useState, useEffect, useCallback } from "react";
import {
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  CircularProgress,
} from "@mui/material";
import { IoAdd, IoTrash, IoRefresh, IoPeople } from "react-icons/io5";

import VuiBox from "components/ui/VuiBox";
import VuiTypography from "components/ui/VuiTypography";
import VuiButton from "components/ui/VuiButton";
import VuiInput from "components/ui/VuiInput";
import VuiBadge from "components/ui/VuiBadge";
import VuiAlert from "components/ui/VuiAlert";

import { adminService } from "services/adminService";
import { useSnackbar } from "notistack";

function KolProfiles() {
  const { enqueueSnackbar } = useSnackbar();

  const [profiles, setProfiles]       = useState([]);
  const [clients, setClients]         = useState([]);
  const [loading, setLoading]         = useState(false);
  const [dialogOpen, setDialogOpen]   = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [submitting, setSubmitting]   = useState(false);

  // Filter
  const [filterCompany, setFilterCompany] = useState("");

  // Add form
  const [form, setForm] = useState({ profileUrl: "", displayAlias: "", companyId: "" });
  const [formError, setFormError] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [profilesRes, clientsRes] = await Promise.allSettled([
        adminService.getKolProfiles(filterCompany || undefined),
        adminService.getClientOrgs(),
      ]);
      if (profilesRes.status === "fulfilled") setProfiles(profilesRes.value.data || []);
      if (clientsRes.status  === "fulfilled") setClients(clientsRes.value.data  || []);
    } catch (e) {
      enqueueSnackbar("Failed to load KOL profiles", { variant: "error" });
    } finally {
      setLoading(false);
    }
  }, [filterCompany, enqueueSnackbar]);

  useEffect(() => { load(); }, [load]);

  const handleAdd = async () => {
    setFormError("");
    if (!form.profileUrl.includes("linkedin.com/in/")) {
      setFormError("Must be a linkedin.com/in/ profile URL");
      return;
    }
    if (!form.displayAlias.trim()) {
      setFormError("Display alias is required");
      return;
    }
    if (!form.companyId) {
      setFormError("Client is required");
      return;
    }
    setSubmitting(true);
    try {
      await adminService.createKolProfile({
        profileUrl:   form.profileUrl.trim(),
        displayAlias: form.displayAlias.trim(),
        companyId:    form.companyId,
      });
      enqueueSnackbar("KOL profile added", { variant: "success" });
      setDialogOpen(false);
      setForm({ profileUrl: "", displayAlias: "", companyId: "" });
      load();
    } catch (e) {
      const status = e?.response?.status;
      if (status === 409) setFormError("This profile URL is already tracked for this client");
      else if (status === 400) setFormError("Invalid URL or missing fields");
      else setFormError("Failed to add profile");
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    setSubmitting(true);
    try {
      await adminService.deleteKolProfile(deleteTarget.kolId);
      enqueueSnackbar(`${deleteTarget.displayAlias} removed`, { variant: "success" });
      setDeleteTarget(null);
      load();
    } catch {
      enqueueSnackbar("Failed to remove profile", { variant: "error" });
    } finally {
      setSubmitting(false);
    }
  };

  const clientName = (id) => clients.find(c => c.id === id)?.name || id;

  return (
    <VuiBox>
      {/* Header */}
      <VuiBox display="flex" justifyContent="space-between" alignItems="center" mb={2}>
        <VuiTypography variant="h6" color="white">
          KOL Profiles
        </VuiTypography>
        <VuiBox display="flex" gap={1}>
          <VuiButton
            variant="outlined"
            color="info"
            size="small"
            onClick={load}
            startIcon={<IoRefresh />}
          >
            Refresh
          </VuiButton>
          <VuiButton
            variant="contained"
            color="info"
            size="small"
            onClick={() => { setFormError(""); setDialogOpen(true); }}
            startIcon={<IoAdd />}
          >
            Add KOL
          </VuiButton>
        </VuiBox>
      </VuiBox>

      {/* Client filter */}
      <VuiBox mb={2} maxWidth={280}>
        <select
          value={filterCompany}
          onChange={e => setFilterCompany(e.target.value)}
          style={{
            background: "rgba(255,255,255,0.06)",
            border: "1px solid rgba(255,255,255,0.15)",
            borderRadius: 6,
            color: "white",
            padding: "6px 10px",
            fontSize: "0.85rem",
            width: "100%",
          }}
        >
          <option value="">All clients</option>
          {clients.map(c => (
            <option key={c.id} value={c.id}>{c.name}</option>
          ))}
        </select>
      </VuiBox>

      {/* Table */}
      {loading ? (
        <VuiBox display="flex" justifyContent="center" py={4}>
          <CircularProgress size={24} sx={{ color: "rgba(255,255,255,0.4)" }} />
        </VuiBox>
      ) : profiles.length === 0 ? (
        <VuiBox
          sx={{
            border: "1px solid rgba(255,255,255,0.1)",
            borderRadius: 2,
            p: 4,
            textAlign: "center",
          }}
        >
          <IoPeople size={28} color="rgba(255,255,255,0.25)" />
          <VuiTypography variant="body2" color="text" mt={1}>
            No KOL profiles tracked yet. Add the first one.
          </VuiTypography>
        </VuiBox>
      ) : (
        <VuiBox
          sx={{
            border: "1px solid rgba(255,255,255,0.1)",
            borderRadius: 2,
            overflow: "hidden",
          }}
        >
          {/* Table header */}
          <VuiBox
            display="grid"
            sx={{
              gridTemplateColumns: "2fr 2fr 1fr 80px",
              gap: 2,
              p: "10px 16px",
              background: "rgba(255,255,255,0.04)",
              borderBottom: "1px solid rgba(255,255,255,0.08)",
            }}
          >
            {["Alias", "Client", "Posts", ""].map((h, i) => (
              <VuiTypography key={i} variant="caption" color="text" fontWeight="bold" textTransform="uppercase">
                {h}
              </VuiTypography>
            ))}
          </VuiBox>

          {/* Rows */}
          {profiles.map((p) => (
            <VuiBox
              key={p.kolId}
              display="grid"
              sx={{
                gridTemplateColumns: "2fr 2fr 1fr 80px",
                gap: 2,
                p: "12px 16px",
                borderBottom: "1px solid rgba(255,255,255,0.05)",
                alignItems: "center",
                "&:last-child": { borderBottom: "none" },
              }}
            >
              <VuiBox display="flex" alignItems="center" gap={1}>
                <VuiTypography variant="body2" color="white" fontWeight="medium">
                  {p.displayAlias}
                </VuiTypography>
                {!p.active && (
                  <VuiBadge variant="standard" badgeContent="inactive" color="error" size="xs" container />
                )}
              </VuiBox>
              <VuiTypography variant="caption" color="text">
                {clientName(p.companyId)}
              </VuiTypography>
              <VuiTypography variant="caption" color="text">
                {p.postCount ?? 0}
              </VuiTypography>
              <VuiBox display="flex" justifyContent="flex-end">
                <VuiButton
                  variant="text"
                  color="error"
                  size="small"
                  onClick={() => setDeleteTarget(p)}
                  sx={{ minWidth: 0, px: 1 }}
                >
                  <IoTrash size={14} />
                </VuiButton>
              </VuiBox>
            </VuiBox>
          ))}
        </VuiBox>
      )}

      {/* Add dialog */}
      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ background: "#111827", color: "white" }}>
          Add KOL Profile
        </DialogTitle>
        <DialogContent sx={{ background: "#111827", pt: "16px !important" }}>
          <VuiBox display="flex" flexDirection="column" gap={2}>
            {formError && <VuiAlert color="error">{formError}</VuiAlert>}

            <VuiBox>
              <VuiTypography variant="caption" color="text" mb={0.5} display="block">
                LinkedIn Profile URL
              </VuiTypography>
              <VuiInput
                placeholder="https://www.linkedin.com/in/..."
                value={form.profileUrl}
                onChange={e => setForm(f => ({ ...f, profileUrl: e.target.value }))}
                fullWidth
              />
            </VuiBox>

            <VuiBox>
              <VuiTypography variant="caption" color="text" mb={0.5} display="block">
                Display Alias
              </VuiTypography>
              <VuiInput
                placeholder="e.g. KOL-001 or NI Surgeon Paris"
                value={form.displayAlias}
                onChange={e => setForm(f => ({ ...f, displayAlias: e.target.value }))}
                fullWidth
              />
              <VuiTypography variant="caption" color="text" mt={0.5} display="block" sx={{ opacity: 0.6 }}>
                No real name — used for internal reference only
              </VuiTypography>
            </VuiBox>

            <VuiBox>
              <VuiTypography variant="caption" color="text" mb={0.5} display="block">
                Client
              </VuiTypography>
              <select
                value={form.companyId}
                onChange={e => setForm(f => ({ ...f, companyId: e.target.value }))}
                style={{
                  background: "rgba(255,255,255,0.06)",
                  border: "1px solid rgba(255,255,255,0.2)",
                  borderRadius: 6,
                  color: "white",
                  padding: "8px 10px",
                  fontSize: "0.875rem",
                  width: "100%",
                }}
              >
                <option value="">Select a client…</option>
                {clients.map(c => (
                  <option key={c.id} value={c.id}>{c.name}</option>
                ))}
              </select>
            </VuiBox>
          </VuiBox>
        </DialogContent>
        <DialogActions sx={{ background: "#111827", px: 3, pb: 2 }}>
          <VuiButton variant="text" color="secondary" onClick={() => setDialogOpen(false)}>
            Cancel
          </VuiButton>
          <VuiButton
            variant="contained"
            color="info"
            onClick={handleAdd}
            disabled={submitting}
            startIcon={submitting ? <CircularProgress size={14} /> : null}
          >
            Add Profile
          </VuiButton>
        </DialogActions>
      </Dialog>

      {/* Delete confirm dialog */}
      <Dialog open={!!deleteTarget} onClose={() => setDeleteTarget(null)} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ background: "#111827", color: "white" }}>
          Remove KOL Profile
        </DialogTitle>
        <DialogContent sx={{ background: "#111827" }}>
          <VuiTypography variant="body2" color="text">
            Remove <strong style={{ color: "white" }}>{deleteTarget?.displayAlias}</strong> from tracking?
            Historical posts are retained.
          </VuiTypography>
        </DialogContent>
        <DialogActions sx={{ background: "#111827", px: 3, pb: 2 }}>
          <VuiButton variant="text" color="secondary" onClick={() => setDeleteTarget(null)}>
            Cancel
          </VuiButton>
          <VuiButton
            variant="contained"
            color="error"
            onClick={handleDelete}
            disabled={submitting}
          >
            Remove
          </VuiButton>
        </DialogActions>
      </Dialog>
    </VuiBox>
  );
}

export default KolProfiles;
